import React from 'react';
import { Card, Stack, Text, Group, Badge, Divider, Alert, ThemeIcon, List } from '@mantine/core';
import { 
  IconAlertTriangle, 
  IconPill, 
  IconPhone, 
  IconUser,
  IconStethoscope,
  IconNotes
} from '@tabler/icons-react';
import type { Student } from '../../../types/api';

interface ChildInfoCardProps {
  child: Student;
}

export const ChildInfoCard: React.FC<ChildInfoCardProps> = ({ child }) => {
  const hasAllergies = child.allergies && child.allergies.length > 0;
  const hasMedicalConditions = child.medical_conditions && child.medical_conditions.length > 0;
  const hasMedications = child.medications && child.medications.length > 0;
  const hasEmergencyContacts = child.emergency_contacts && child.emergency_contacts.length > 0;

  return (
    <Stack gap="md">
      {/* Allergies Alert */}
      {hasAllergies && (
        <Alert 
          icon={<IconAlertTriangle size={16} />} 
          title="Allergies" 
          color="red"
          variant="light"
        >
          <List size="sm" spacing="xs">
            {child.allergies?.map((allergy, index) => (
              <List.Item key={index}>
                <strong>{allergy.allergen}</strong>
                {allergy.severity && (
                  <Badge size="xs" color="red" variant="light" ml="xs">
                    {allergy.severity}
                  </Badge>
                )}
                {allergy.notes && <Text size="xs" c="dimmed">{allergy.notes}</Text>}
              </List.Item>
            ))}
          </List>
        </Alert>
      )}

      {/* Medical Information */}
      <Card withBorder>
        <Stack gap="md">
          <Group>
            <ThemeIcon size="lg" variant="light" color="blue">
              <IconStethoscope size={20} />
            </ThemeIcon>
            <Text fw={600}>Medical Information</Text>
          </Group>

          {/* Medical Conditions */}
          {hasMedicalConditions && (
            <>
              <div>
                <Text size="sm" fw={500} mb="xs">Medical Conditions</Text>
                <Stack gap="xs">
                  {child.medical_conditions?.map((condition, index) => (
                    <Card key={index} p="xs" withBorder>
                      <Text size="sm" fw={500}>{condition.name}</Text>
                      {condition.notes && (
                        <Text size="xs" c="dimmed">{condition.notes}</Text>
                      )}
                    </Card>
                  ))}
                </Stack>
              </div>
              <Divider />
            </>
          )}

          {/* Medications */}
          {hasMedications && (
            <>
              <div>
                <Group gap="xs" mb="xs">
                  <IconPill size={16} />
                  <Text size="sm" fw={500}>Current Medications</Text>
                </Group>
                <Stack gap="xs">
                  {child.medications?.map((medication, index) => (
                    <Card key={index} p="xs" withBorder>
                      <Text size="sm" fw={500}>{medication.name}</Text>
                      <Text size="xs" c="dimmed">
                        Dosage: {medication.dosage} | Schedule: {medication.schedule}
                      </Text>
                      {medication.notes && (
                        <Text size="xs" c="dimmed" mt={4}>{medication.notes}</Text>
                      )}
                    </Card>
                  ))}
                </Stack>
              </div>
              <Divider />
            </>
          )}

          {/* Special Notes */}
          {child.special_needs && (
            <div>
              <Group gap="xs" mb="xs">
                <IconNotes size={16} />
                <Text size="sm" fw={500}>Special Needs / Notes</Text>
              </Group>
              <Text size="sm" c="dimmed">{child.special_needs}</Text>
            </div>
          )}

          {!hasMedicalConditions && !hasMedications && !child.special_needs && (
            <Text size="sm" c="dimmed" ta="center">No medical information on file</Text>
          )}
        </Stack>
      </Card>

      {/* Emergency Contacts */}
      <Card withBorder>
        <Stack gap="md">
          <Group>
            <ThemeIcon size="lg" variant="light" color="red">
              <IconPhone size={20} />
            </ThemeIcon>
            <Text fw={600}>Emergency Contacts</Text>
          </Group>

          {hasEmergencyContacts ? (
            <Stack gap="sm">
              {child.emergency_contacts?.map((contact, index) => (
                <Card key={index} p="sm" withBorder>
                  <Group justify="space-between" wrap="nowrap">
                    <div>
                      <Group gap="xs">
                        <IconUser size={16} />
                        <Text size="sm" fw={500}>{contact.name}</Text>
                        <Badge size="xs" variant="light">
                          {contact.relationship}
                        </Badge>
                      </Group>
                      <Text size="xs" c="dimmed" mt={4}>
                        {contact.phone}
                      </Text>
                      {contact.email && (
                        <Text size="xs" c="dimmed">
                          {contact.email}
                        </Text>
                      )}
                    </div>
                  </Group>
                </Card>
              ))}
            </Stack>
          ) : (
            <Text size="sm" c="dimmed" ta="center">No emergency contacts on file</Text>
          )}
        </Stack>
      </Card>
    </Stack>
  );
};

export default ChildInfoCard;